import { BoundaryCharacteristic, Utils } from '@cadstrom/sigma-engine-shared';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import Popover from '@mui/material/Popover';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useMemo, useState } from 'react';

import { useActiveDesignContext } from '#components/cad-renderer/context/active-design-context.js';
import { useCoreModelContext } from '#components/cad-renderer/context/core-model-context.js';
import { useZIndex, ZIndexProvider } from '#components/cad-renderer/context/z-index-context.js';
import { ModelModuleChip } from '#components/model-module-chip/model-module-chip.js';
import { ModelText } from '#components/model-text/model-text.js';
import { getCharacteristicSummaryString } from './utils.js';

import type { INetStats } from '@cadstrom/sigma-engine-shared';
import type { ReactNode } from 'react';

type PropertyEntry = ReactNode[];

const hideMetadataKeys = ['explanation'];

export type NetChipPopperProps = {
	net: string;
	anchorEl: HTMLElement;
	onClose?: () => void;
};

export function NetChipPopper(props: NetChipPopperProps) {
	const { coreModel } = useCoreModelContext();
	const { activeDesign } = useActiveDesignContext();
	const [value, setValue] = useState('1');
	const zIndex = useZIndex('popover');

	const modelType = activeDesign.modelType;
	const netProperties: PropertyEntry[] = useMemo(() => {
		let entries: PropertyEntry[] = [];

		if (coreModel.netStats) {
			const stats = coreModel.netStats;
			entries = entries.concat(getNetStatPropertyEntries(props.net, stats as Record<string, INetStats>));
		}

		const net = coreModel.globalNets[props.net] ?? activeDesign.nets[props.net];
		if (!net || typeof net !== 'object') {
			return entries;
		}

		if (net.metadata) {
			for (const [key, value] of Object.entries(net.metadata)) {
				if (value !== undefined && !hideMetadataKeys.includes(key)) {
					entries.push([key, value.toString()]);
				}
			}
		}

		return entries;
	}, [activeDesign.nets, coreModel.globalNets, coreModel.netStats, props.net]);

	const netConnections: PropertyEntry[] = useMemo(() => {
		const entries: PropertyEntry[] = [['Component', 'Signal', modelType === 'pcb' ? 'Pad' : 'Pin']];
		const netNames: string[] = [props.net];
		const modulePins = new Set<string>();
		const modulesAndPins: [string, string, string][] = []; // [moduleRef, signal, pinName]

		// This manually collecting of nets by looking at designs gets around current limitations in net parsing.
		// eslint-disable-next-line @typescript-eslint/prefer-for-of
		for (let i = 0; i < netNames.length; i++) {
			const netName = netNames[i];
			for (const design of Object.values(coreModel.designs)) {
				for (const net of Object.values(design.nets)) {
					if (net.name !== netName && net.localName !== netName && !net.netLabels?.some((l) => l.label === netName)) {
						continue;
					}

					if (!netNames.includes(net.name)) {
						netNames.push(net.name);
					}

					for (const connection of net.connections) {
						const designModule = design.modules.find((m) => m.ref === connection.moduleRef);
						if (!designModule) {
							continue;
						}

						const useName = designModule.fullModuleName ?? designModule.name;
						const ref = `${useName}::${connection.pinRef}`;
						const component = coreModel.components[designModule.componentRef];
						let signal: string;
						let pinName: string;
						if (design.modelType === 'schematic') {
							const pin = component?.symbols
								.find((s) => s.ref === designModule.footprintRef)
								?.pins.find((p) => p.ref === connection.pinRef);
							pinName = pin?.name ?? connection.pinRef;
							signal = pin?.name ?? connection.pinRef;
							signal = component?.signals.find((s) => s.pinRefs.includes(connection.pinRef))?.name ?? '?';
						} else {
							const pin = component?.footprints
								.find((f) => f.ref === designModule.footprintRef)
								?.pads.find((p) => p.ref === connection.pinRef);
							pinName = pin?.name ?? connection.pinRef;
							signal = component?.signals.find((s) => s.padRefs.includes(connection.pinRef))?.name ?? '?';
						}

						if (!modulePins.has(ref)) {
							modulePins.add(ref);
							modulesAndPins.push([useName, signal ?? '', pinName]);
						}
					}
				}
			}
		}

		// Use natural sort to sort first by reference designator, then by pin number
		modulesAndPins.sort((a, b) => {
			const moduleCompare = Utils.naturalStringSort(a[0], b[0]);
			if (moduleCompare !== 0) {
				return moduleCompare;
			}
			return Utils.naturalStringSort(a[1] ?? a[2] ?? '', b[1] ?? b[2] ?? '');
		});

		// Add the entries
		for (const [moduleRef, signal, pinRef] of modulesAndPins) {
			entries.push([
				<ModelModuleChip key={`${moduleRef}:${pinRef}`} moduleReferenceDesignator={moduleRef} />,
				<ModelText key={`${moduleRef}:${pinRef}:signal`} value={signal} />,
				<ModelText key={`${moduleRef}:${pinRef}:pin`} value={pinRef} />,
			]);
		}

		return entries;
	}, [coreModel.components, coreModel.designs, modelType, props.net]);

	const handleChange = (event: React.SyntheticEvent, newValue: string) => {
		setValue(newValue);
	};

	return (
		<Popover
			open
			anchorEl={props.anchorEl}
			anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
			transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
			onClose={() => props.onClose?.()}
			style={{ zIndex }}
		>
			<ZIndexProvider>
				<div className='mx-2 mt-2 flex justify-center pl-px'>
					<ModelText value={props.net} className='text-xl font-semibold' />
					<div className='grow' />
				</div>
				<div className='mx-2 mt-0'>{renderPropertyTable(netProperties)}</div>
				{netConnections.length <= 1 ?
					<p className='text-sm text-gray-a8 italic'>No net connection information available</p>
				:	<TabContext value={value}>
						<div className='mx-2 border-b border-gray-a4'>
							<TabList onChange={handleChange} aria-label='Net Property Tabs'>
								<Tab label='Net Connections' value='1' />
							</TabList>
						</div>
						<div className='-mx-px flex flex-col overflow-auto'>
							<TabPanel value='1' className='p-0'>
								{renderPropertyTable(netConnections, true)}
							</TabPanel>
						</div>
					</TabContext>
				}
			</ZIndexProvider>
		</Popover>
	);
}

function renderPropertyTable(moduleProperties: PropertyEntry[], firstRowHeader = false) {
	if (moduleProperties.length <= (firstRowHeader ? 1 : 0)) {
		return null;
	}

	return (
		<Table size='small' stickyHeader>
			{firstRowHeader && (
				<TableHead>
					<TableRow>{moduleProperties[0]?.map((value, i) => <TableCell key={i}>{value}</TableCell>)}</TableRow>
				</TableHead>
			)}
			<TableBody>
				{moduleProperties
					.filter(([mod]) => mod !== null)
					.map(
						(values, index) =>
							(!firstRowHeader || index !== 0) && (
								<TableRow key={index}>
									{values.map((value, i) => (
										<TableCell key={i}>{value}</TableCell>
									))}
								</TableRow>
							),
					)}
			</TableBody>
		</Table>
	);
}

function getNetStatPropertyEntries(
	net: string,
	netStats: { [netName: string]: INetStats },
): ConcatArray<PropertyEntry> {
	const entries: PropertyEntry[] = [];
	const netStat = netStats[net];
	if (!netStat) {
		return entries;
	}

	for (const characteristic of netStat.characteristics) {
		const valueStr = getCharacteristicSummaryString(characteristic.characteristic, characteristic.value);
		if (characteristic.value.value !== undefined) {
			entries.push([
				<p key={characteristic.characteristic} className='capitalize'>
					{characteristicsNominalStrings[characteristic.characteristic]}
				</p>,
				valueStr,
			]);
		} else {
			entries.push([
				<p key={characteristic.characteristic} className='capitalize'>
					{characteristicsRangeStrings[characteristic.characteristic]}
				</p>,
				valueStr,
			]);
		}
	}

	return entries;
}

const characteristicsNominalStrings: Record<BoundaryCharacteristic, string> = {
	[BoundaryCharacteristic.Voltage]: 'nominal voltage',
	[BoundaryCharacteristic.Current]: 'nominal current',
	[BoundaryCharacteristic.Temperature]: 'nominal temperature',
	[BoundaryCharacteristic.TemperatureKelvin]: 'nominal temperature',
	[BoundaryCharacteristic.Power]: 'nominal power',
	[BoundaryCharacteristic.Frequency]: 'nominal frequency',
	[BoundaryCharacteristic.Inductance]: 'nominal inductance',
	[BoundaryCharacteristic.Resistance]: 'nominal resistance',
	[BoundaryCharacteristic.Capacitance]: 'nominal capacitance',
};
const characteristicsRangeStrings: Record<BoundaryCharacteristic, string> = {
	[BoundaryCharacteristic.Voltage]: 'voltage range',
	[BoundaryCharacteristic.Current]: 'current range',
	[BoundaryCharacteristic.Temperature]: 'temperature range',
	[BoundaryCharacteristic.TemperatureKelvin]: 'temperature range',
	[BoundaryCharacteristic.Power]: 'power range',
	[BoundaryCharacteristic.Frequency]: 'frequency range',
	[BoundaryCharacteristic.Inductance]: 'inductance range',
	[BoundaryCharacteristic.Resistance]: 'resistance range',
	[BoundaryCharacteristic.Capacitance]: 'capacitance range',
};
